import React, { useState, useEffect } from "react";

const sections = [
  { id: 1, label: "1 - Operator Certification" },
  { id: 2, label: "2 - Distribution Networks" },
  { id: 3, label: "3 - Distribution Storage" },
  { id: 4, label: "4 - Water Quality & Monitoring" },
];

function getQuestions(questionBank, selectedSections, numQuestions) {
  let filtered = questionBank.filter(q => selectedSections.includes(q.section));
  for (let i = filtered.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [filtered[i], filtered[j]] = [filtered[j], filtered[i]];
  }
  return filtered.slice(0, Math.min(numQuestions, filtered.length));
}

export default function FlashcardApp() {
  const [questionBank, setQuestionBank] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(2); // Start on Settings tab
  const [selectedSections, setSelectedSections] = useState([1,2,3,4]);
  const [numQuestions, setNumQuestions] = useState(5);
  const [quiz, setQuiz] = useState([]);
  const [current, setCurrent] = useState(0);
  const [results, setResults] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [showHint, setShowHint] = useState(false);
  const [vocabInput, setVocabInput] = useState("");
  const [settingsWarning, setSettingsWarning] = useState("");

  // Fetch questions on mount
  useEffect(() => {
    fetch("/questions.json")
      .then(res => res.json())
      .then(data => {
        setQuestionBank(data);
        setQuiz(getQuestions(data, selectedSections, numQuestions));
        setLoading(false);
      });
  }, []);

  // Handlers
  const handleSectionToggle = (sectionId) => {
    setSelectedSections(prev => prev.includes(sectionId)
      ? prev.filter(id => id !== sectionId)
      : [...prev, sectionId]
    );
  };

  const handleNumQuestions = (val) => setNumQuestions(val);

  const handleSettingsSave = () => {
    const qs = getQuestions(questionBank, selectedSections.length ? selectedSections : [1,2,3,4], numQuestions);
    if (qs.length < numQuestions) {
      setSettingsWarning(
        `Only ${qs.length} questions available for your selected sections.`
      );
    } else {
      setSettingsWarning("");
    }
    setQuiz(qs);
    setCurrent(0);
    setResults([]);
    setInputValue("");
    setVocabInput("");
    setShowHint(false);
    setTab(0); // Move to Questions tab!
  };

  const handleReset = () => {
    handleSettingsSave();
  };

  const currentQ = quiz[current];
  const isLast = current === quiz.length - 1;

  // Answer logic
  const handleAnswer = (guess) => {
    let isCorrect = false;
    let userAnswer = null;
    if (currentQ.type === "multiple") {
      isCorrect = guess === currentQ.answer;
      userAnswer = guess;
    } else if (currentQ.type === "math") {
      isCorrect = parseFloat(inputValue) === currentQ.solution;
      userAnswer = inputValue;
    } else if (currentQ.type === "vocab") {
      isCorrect = vocabInput.trim().toLowerCase() === currentQ.term.trim().toLowerCase();
      userAnswer = vocabInput;
    }
    setResults([...results, {correct: isCorrect, userAnswer, question: currentQ}]);
    setInputValue("");
    setVocabInput("");
    setShowHint(false);
    if (!isLast) setCurrent(current + 1);
  };

  // Stats logic:
  const correct = results.filter(r => r.correct).length;
  const incorrect = results.length - correct;
  const left = quiz.length - results.length;
  const wrongAnswers = results
    .map((res, i) => ({...res, idx: i+1}))
    .filter(r => !r.correct);

  if (loading) return <div style={{maxWidth:"40rem",margin:"2rem auto",padding:"2rem",fontSize:"1.5rem"}}>Loading questions...</div>;

  return (
    <div style={{maxWidth:"40rem",margin:"1.5rem auto",padding:"1.5rem",background:"#fff",borderRadius:"1.5rem",boxShadow:"0 2px 12px rgba(0,0,0,0.09)"}}>
      {/* Tabs */}
      <div style={{display:"flex",borderBottom:"1px solid #ddd",marginBottom:"1.2rem"}}>
        {["Questions","Stats","Settings"].map((t, i) => (
          <button key={t}
            style={{
              flex:1,
              padding:"0.5rem",
              fontSize:"1.25rem",
              fontWeight:600,
              borderBottom:i===tab?"3px solid #2563eb":"3px solid transparent",
              color:i===tab?"#2563eb":"#888",
              background:i===tab?"#f8fafc":"#fff",
              cursor:"pointer",
              transition:"all .18s"
            }}
            onClick={() => setTab(i)}>{t}</button>
        ))}
      </div>

      {/* Tab: Questions */}
      {tab === 0 && quiz.length > 0 && currentQ && (
        <div>
          <div style={{marginBottom:"0.6rem",textAlign:"right",color:"#888",fontSize:"0.95rem"}}>
            Question {current+1} of {quiz.length}
          </div>
          <div style={{background:"#f6f6fd",padding:"2rem 1rem",borderRadius:"1rem",marginBottom:"1.25rem",textAlign:"center"}}>
            {currentQ.type === "multiple" && (
              <>
                <div style={{fontWeight:"bold",fontSize:"1.15rem",marginBottom:"1rem"}}>{currentQ.question}</div>
                <div style={{display:"grid",gap:"0.75rem"}}>
                  {currentQ.choices.map((c, idx) => (
                    <button key={c}
                      style={{
                        padding:"0.8rem",
                        borderRadius:"0.7rem",
                        border:"1px solid #bbb",
                        background:"#fff",
                        width:"100%",
                        fontWeight:500,
                        cursor:"pointer"
                      }}
                      onClick={()=>handleAnswer(idx)}
                    >{c}</button>
                  ))}
                </div>
              </>
            )}
            {currentQ.type === "vocab" && (
              <>
                <div style={{fontWeight:"bold",fontSize:"1.15rem",marginBottom:"1rem"}}>Definition</div>
                <div style={{marginBottom:"1rem",fontSize:"1.15rem"}}>{currentQ.definition}</div>
                <input
                  type="text"
                  style={{border:"1px solid #ccc",padding:"0.6rem",borderRadius:"0.6rem",width:"70%",marginBottom:"0.5rem",textAlign:"center"}}
                  value={vocabInput}
                  onChange={e=>setVocabInput(e.target.value)}
                  placeholder="Type the term..."
                  onKeyDown={e=>e.key==="Enter"&&handleAnswer()}
                />
                <div>
                  <button style={{
                    padding:"0.7rem 2.2rem",
                    background:"#2563eb",
                    color:"#fff",
                    border:"none",
                    borderRadius:"0.8rem",
                    marginTop:"0.2rem",
                    cursor:"pointer"
                  }} onClick={()=>handleAnswer()}>Submit</button>
                </div>
                <div style={{marginTop:"1rem",color:"#aaa",fontSize:"0.9rem"}}>(Spelling counts. Case doesn’t.)</div>
              </>
            )}
            {currentQ.type === "math" && (
              <>
                <div style={{fontWeight:"bold",fontSize:"1.15rem",marginBottom:"0.7rem"}}>{currentQ.question}</div>
                {!showHint && (
                  <button style={{
                    background:"none",
                    color:"#2563eb",
                    border:"1px solid #2563eb",
                    borderRadius:"0.7rem",
                    padding:"0.4rem 1.6rem",
                    marginBottom:"0.8rem",
                    cursor:"pointer"
                  }} onClick={()=>setShowHint(true)}>Show Hint</button>
                )}
                {showHint && (
                  <div style={{color:"#888",marginBottom:"0.7rem"}}>Hint: {currentQ.hint}</div>
                )}
                <input
                  type="text"
                  style={{border:"1px solid #ccc",padding:"0.6rem",borderRadius:"0.6rem",width:"30%",marginBottom:"0.5rem",textAlign:"center"}}
                  value={inputValue}
                  onChange={e=>setInputValue(e.target.value)}
                  placeholder="Enter answer"
                  onKeyDown={e=>e.key==="Enter"&&handleAnswer()}
                />
                <div>
                  <button style={{
                    padding:"0.7rem 2.2rem",
                    background:"#2563eb",
                    color:"#fff",
                    border:"none",
                    borderRadius:"0.8rem",
                    marginTop:"0.2rem",
                    cursor:"pointer"
                  }} onClick={()=>handleAnswer()}>Submit</button>
                </div>
              </>
            )}
          </div>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <button onClick={()=>setTab(1)}
              style={{
                padding:"0.7rem 1.6rem",
                background:"#e6eaff",
                color:"#2563eb",
                border:"none",
                borderRadius:"0.8rem",
                fontWeight:600,
                cursor:"pointer"
              }}>View Stats</button>
            {isLast && results.length===quiz.length && (
              <button onClick={handleReset}
                style={{
                  padding:"0.7rem 1.6rem",
                  background:"#d8f6de",
                  color:"#256e3b",
                  border:"none",
                  borderRadius:"0.8rem",
                  fontWeight:600,
                  cursor:"pointer"
                }}>Restart Quiz</button>
            )}
          </div>
        </div>
      )}

      {/* Tab: Stats */}
      {tab === 1 && (
        <div style={{textAlign:"center",marginTop:"2rem"}}>
          <div style={{fontSize:"2rem",fontWeight:700}}>Quiz Stats</div>
          <div style={{display:"flex",justifyContent:"center",gap:"2.5rem",fontSize:"1.1rem",margin:"1.2rem 0"}}>
            <div>
              <div style={{fontWeight:700}}>Correct</div>
              <div>{correct}</div>
            </div>
            <div>
              <div style={{fontWeight:700}}>Incorrect</div>
              <div>{incorrect}</div>
            </div>
            <div>
              <div style={{fontWeight:700}}>Left</div>
              <div>{left}</div>
            </div>
          </div>
          {wrongAnswers.length > 0 && (
            <div style={{marginTop:"2.2rem",textAlign:"left"}}>
              <div style={{fontWeight:700,fontSize:"1.1rem",marginBottom:"0.7rem"}}>Incorrect Answers Review</div>
              <ul style={{listStyle:"none",padding:"0"}}>
                {wrongAnswers.map((item, i) => (
                  <li key={i} style={{background:"#fbeaea",borderRadius:"1rem",padding:"1.2rem",marginBottom:"1.2rem",border:"1px solid #f6cccc"}}>
                    <div style={{marginBottom:"0.6rem",fontWeight:600}}>Question {item.idx}:</div>
                    <div style={{marginBottom:"0.7rem"}}>
                      {item.question.type === "multiple" && item.question.question}
                      {item.question.type === "math" && item.question.question}
                      {item.question.type === "vocab" && item.question.definition}
                    </div>
                    <div><span style={{fontWeight:700}}>Your Answer: </span>
                      {item.question.type === "multiple" && typeof item.userAnswer === 'number' && item.question.choices[item.userAnswer] !== undefined ? item.question.choices[item.userAnswer] : String(item.userAnswer)}</div>
                    <div><span style={{fontWeight:700}}>Correct Answer: </span>
                      {item.question.type === "multiple" && item.question.choices[item.question.answer]}
                      {item.question.type === "vocab" && item.question.term}
                      {item.question.type === "math" && item.question.solution}
                    </div>
                    {item.question.explanation && <div style={{color:"#888",marginTop:"0.5rem"}}>Why: {item.question.explanation}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <button onClick={handleReset}
            style={{
              padding:"0.7rem 2.5rem",
              background:"#f0f3fd",
              color:"#2563eb",
              border:"none",
              borderRadius:"0.8rem",
              fontWeight:600,
              cursor:"pointer",
              marginTop:"1.5rem"
            }}>Reset Quiz</button>
        </div>
      )}

      {/* Tab: Settings */}
      {tab === 2 && (
        <div style={{marginTop:"0.5rem"}}>
          <div style={{marginBottom:"1rem",fontWeight:600,fontSize:"1.13rem"}}>Select Sections to Study</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1rem",marginBottom:"2rem"}}>
            {sections.map(s => (
              <label key={s.id}
                style={{
                  display:"flex",alignItems:"center",gap:"0.6rem",
                  padding:"0.7rem",
                  borderRadius:"1rem",
                  border:selectedSections.includes(s.id)?"2px solid #2563eb":"2px solid #eee",
                  background:selectedSections.includes(s.id)?"#e9f2ff":"#fff",
                  fontWeight:500,
                  cursor:"pointer"
                }}>
                <input type="checkbox"
                  checked={selectedSections.includes(s.id)}
                  onChange={()=>handleSectionToggle(s.id)}
                  style={{accentColor:"#2563eb"}}
                />
                {s.label}
              </label>
            ))}
          </div>
          <div style={{marginBottom:"1.1rem"}}>
            <label style={{display:"block",marginBottom:"0.5rem",fontWeight:600}}>Number of Questions</label>
            <select value={numQuestions} onChange={e=>handleNumQuestions(Number(e.target.value))}
              style={{border:"1px solid #bbb",padding:"0.7rem",borderRadius:"0.8rem"}}>
              {[5,10,15,20,25,30,35,40,45,50].map(n=>(
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          {settingsWarning && (
            <div style={{marginBottom:"1.2rem",padding:"0.7rem",background:"#fffbe7",color:"#ae7400",borderRadius:"0.7rem"}}>
              {settingsWarning}
            </div>
          )}
          <button onClick={handleSettingsSave}
            style={{
              width:"100%",
              padding:"0.9rem",
              background:"#2563eb",
              color:"#fff",
              border:"none",
              borderRadius:"1rem",
              fontWeight:600,
              fontSize:"1.1rem",
              cursor:"pointer"
            }}>Start Quiz</button>
        </div>
      )}
    </div>
  );
}
